import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route scroll memory.
 *
 * - Records window.scrollY for the active pathname as the user scrolls.
 * - On navigation, restores the saved scroll for the destination path, or
 *   resets to the top if it has never been visited.
 *
 * Mounted once inside <Router> in App.tsx.
 */
const POSITIONS = new Map<string, number>();

export default function ScrollManager() {
  const { pathname } = useLocation();
  const currentPathRef = useRef(pathname);

  // Keep the live pathname accessible to the scroll listener without
  // recreating the listener on every navigation.
  useEffect(() => {
    currentPathRef.current = pathname;
  }, [pathname]);

  // One scroll listener for the lifetime of the app.
  useEffect(() => {
    const onScroll = () => {
      POSITIONS.set(currentPathRef.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Restore (or reset) on each navigation. Two animation frames let the new
  // route commit and layout (including image reflows) settle before scrolling.
  useEffect(() => {
    const saved = POSITIONS.get(pathname);
    const target = typeof saved === 'number' ? saved : 0;

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        window.scrollTo(0, target);
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [pathname]);

  return null;
}
